/**
 * Maps LangHub's 40 display locales onto the 15 Cloudscape publishes message packs for.
 *
 * An explicit table rather than passing our tag through, because Cloudscape's formatter widens a
 * tag by stripping subtags (`de-DE` → `de`) and, finding nothing, silently returns each component's
 * built-in English default — so `pt-PT` would get English where the `pt-BR` pack is a far better
 * answer, with no record that we chose it. Falling back to English here is cosmetic, unlike a
 * missing LangHub pack, which is why this resolves rather than throws.
 *
 * See `.agents/planning/2026-10-12-panther-localization/design/design.md` §4.5.
 */

export const CLOUDSCAPE_FALLBACK_LOCALE = 'en';

/** Exported so a test can assert every value this module produces is loadable — otherwise a bad
 * tag resolves to an empty message array, indistinguishable from an untranslated locale. */
export const CLOUDSCAPE_LOCALES: readonly string[] = [
  'ar',
  'de',
  'en',
  'en-GB',
  'es',
  'fr',
  'id',
  'it',
  'ja',
  'ko',
  'pt-BR',
  'th',
  'tr',
  'zh-CN',
  'zh-TW',
];

/** Full-tag mappings, for the locales where the region rather than the language decides. */
const TAG_TO_CLOUDSCAPE_LOCALE: Readonly<Record<string, string>> = {
  // Cloudscape's `en-GB` pack differs from `en` in 18 of 797 keys, all conventions these markets
  // share with the UK: "Customise", "cancelled", "dialogue", "pop-over".
  'en-AE': 'en-GB',
  'en-AU': 'en-GB',
  'en-GB': 'en-GB',
  'en-IE': 'en-GB',
  'en-IN': 'en-GB',
  'en-SG': 'en-GB',
  'en-ZA': 'en-GB',

  // Canadian English takes American conventions in software, and `en` is what the AWS console
  // already shows these users.
  'en-CA': 'en',
  'en-US': 'en',

  // No `pt-PT` pack exists; the two are mutually intelligible, so a Portuguese reader gets
  // Portuguese chrome instead of English. An unrelated language would not justify this.
  'pt-PT': 'pt-BR',

  // Chinese is chosen by script, so each region is listed rather than widened.
  'zh-CN': 'zh-CN',
  'zh-HK': 'zh-TW',
  'zh-MO': 'zh-TW',
  'zh-TW': 'zh-TW',
};

/**
 * Language-subtag mappings, applied when no full-tag entry matches. Mirrors Cloudscape's published
 * set, so it also carries languages LangHub does not offer; a language absent from it resolves to
 * {@link CLOUDSCAPE_FALLBACK_LOCALE}. The 14 unmapped LangHub languages (bn, cs, da, he, hi, kn,
 * ml, mr, nb, nl, pl, sv, ta, te) therefore get English chrome — pairing unrelated languages reads
 * as a bug where English reads as untranslated. Hebrew still gets RTL layout, which comes from
 * `isRTL` and `postcss-rtl`, not from this table.
 */
const LANGUAGE_TO_CLOUDSCAPE_LOCALE: Readonly<Record<string, string>> = {
  ar: 'ar',
  de: 'de',
  en: 'en',
  es: 'es',
  fr: 'fr',
  id: 'id',
  it: 'it',
  ja: 'ja',
  ko: 'ko',
  pt: 'pt-BR',
  th: 'th',
  tr: 'tr',
  zh: 'zh-CN',
};

/**
 * Resolution order: exact tag, then language subtag, then English. The result must be passed as
 * BOTH the `importMessages` argument and the `I18nProvider` `locale` prop — packs are keyed
 * internally by their own locale, so handing the provider our tag while loading a mapped pack
 * (`pt-PT` against `pt-BR`) matches nothing and silently renders English.
 *
 * Matching is case-insensitive because BCP 47 is, and a locale read back out of `localStorage` may
 * not carry the recommended casing.
 */
export const toCloudscapeLocale = (locale: string): string => {
  const parts = locale.split('-');
  const language = parts[0].toLowerCase();
  const normalizedTag =
    parts.length > 1
      ? `${language}-${parts.slice(1).join('-').toUpperCase()}`
      : language;

  return (
    TAG_TO_CLOUDSCAPE_LOCALE[normalizedTag] ??
    LANGUAGE_TO_CLOUDSCAPE_LOCALE[language] ??
    CLOUDSCAPE_FALLBACK_LOCALE
  );
};
